import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import apiService from '../services/api';
import '../styles/Admin.css';

const Admin = () => {
  const { user } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Notification form state
  const [notificationForm, setNotificationForm] = useState({
    type: 'system',
    title: '',
    message: '',
    userId: '',
    data: {}
  });

  // Broadcast form state
  const [broadcastForm, setBroadcastForm] = useState({
    type: 'system',
    title: '',
    message: '',
    data: {}
  });

  // Feature announcement form state
  const [featureForm, setFeatureForm] = useState({
    featureName: '',
    description: '',
    url: ''
  });

  useEffect(() => {
    if (user && user.email === 'admin@prepright.com') {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.request('/admin/users');
      if (response.success) {
        setUsers(response.users);
      }
    } catch (error) {
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.request('/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify(notificationForm)
      });

      if (response.success) {
        showMessage('Notification sent successfully!');
        setNotificationForm({
          type: 'system',
          title: '',
          message: '',
          userId: '',
          data: {}
        });
      }
    } catch (error) {
      showMessage('Failed to send notification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBroadcastNotification = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.request('/admin/notifications/broadcast', {
        method: 'POST',
        body: JSON.stringify(broadcastForm)
      });

      if (response.success) {
        showMessage(`Notification sent to ${response.count} users!`);
        setBroadcastForm({
          type: 'system',
          title: '',
          message: '',
          data: {}
        });
      }
    } catch (error) {
      showMessage('Failed to broadcast notification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureAnnouncement = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await apiService.request('/admin/notifications/feature', {
        method: 'POST',
        body: JSON.stringify(featureForm)
      });

      if (response.success) {
        showMessage(`Feature announcement sent to ${response.count} users!`);
        setFeatureForm({
          featureName: '',
          description: '',
          url: ''
        });
      }
    } catch (error) {
      showMessage('Failed to send feature announcement', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  if (!user || user.email !== 'admin@prepright.com') {
    return (
      <div className="admin-container">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You need admin privileges to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Send notifications to users</p>
      </div>

      {message && (
        <div className={`admin-message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="admin-sections">
        {/* Send to Specific User */}
        <div className="admin-section">
          <h2>Send to Specific User</h2>
          <form onSubmit={handleSendNotification} className="admin-form">
            <div className="form-group">
              <label>User ID:</label>
              <select 
                value={notificationForm.userId} 
                onChange={(e) => setNotificationForm({...notificationForm, userId: e.target.value})}
                required
              >
                <option value="">Select User</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email}) - Level {user.level}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Type:</label>
              <select 
                value={notificationForm.type} 
                onChange={(e) => setNotificationForm({...notificationForm, type: e.target.value})}
                required
              >
                <option value="system">System</option>
                <option value="achievement">Achievement</option>
                <option value="level_up">Level Up</option>
                <option value="like">Like</option>
                <option value="comment">Comment</option>
              </select>
            </div>

            <div className="form-group">
              <label>Title:</label>
              <input 
                type="text" 
                value={notificationForm.title} 
                onChange={(e) => setNotificationForm({...notificationForm, title: e.target.value})}
                placeholder="Notification title"
                required
              />
            </div>

            <div className="form-group">
              <label>Message:</label>
              <textarea 
                value={notificationForm.message} 
                onChange={(e) => setNotificationForm({...notificationForm, message: e.target.value})}
                placeholder="Notification message"
                required
                rows="3"
              />
            </div>

            <button type="submit" disabled={loading} className="admin-btn">
              {loading ? 'Sending...' : 'Send Notification'}
            </button>
          </form>
        </div>

        {/* Broadcast to All Users */}
        <div className="admin-section">
          <h2>Broadcast to All Users</h2>
          <form onSubmit={handleBroadcastNotification} className="admin-form">
            <div className="form-group">
              <label>Type:</label>
              <select 
                value={broadcastForm.type} 
                onChange={(e) => setBroadcastForm({...broadcastForm, type: e.target.value})}
                required
              >
                <option value="system">System</option>
                <option value="achievement">Achievement</option>
                <option value="level_up">Level Up</option>
              </select>
            </div>

            <div className="form-group">
              <label>Title:</label>
              <input 
                type="text" 
                value={broadcastForm.title} 
                onChange={(e) => setBroadcastForm({...broadcastForm, title: e.target.value})}
                placeholder="Broadcast title"
                required
              />
            </div>

            <div className="form-group">
              <label>Message:</label>
              <textarea 
                value={broadcastForm.message} 
                onChange={(e) => setBroadcastForm({...broadcastForm, message: e.target.value})}
                placeholder="Broadcast message"
                required
                rows="3"
              />
            </div>

            <button type="submit" disabled={loading} className="admin-btn">
              {loading ? 'Broadcasting...' : 'Broadcast to All Users'}
            </button>
          </form>
        </div>

        {/* Feature Announcement */}
        <div className="admin-section">
          <h2>Feature Announcement</h2>
          <form onSubmit={handleFeatureAnnouncement} className="admin-form">
            <div className="form-group">
              <label>Feature Name:</label>
              <input 
                type="text" 
                value={featureForm.featureName} 
                onChange={(e) => setFeatureForm({...featureForm, featureName: e.target.value})}
                placeholder="e.g., Recipe Sharing"
                required
              />
            </div>

            <div className="form-group">
              <label>Description:</label>
              <textarea 
                value={featureForm.description} 
                onChange={(e) => setFeatureForm({...featureForm, description: e.target.value})}
                placeholder="Describe the new feature"
                required
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>URL (optional):</label>
              <input 
                type="url" 
                value={featureForm.url} 
                onChange={(e) => setFeatureForm({...featureForm, url: e.target.value})}
                placeholder="https://prepright.com/recipes"
              />
            </div>

            <button type="submit" disabled={loading} className="admin-btn">
              {loading ? 'Announcing...' : 'Announce Feature'}
            </button>
          </form>
        </div>
      </div>

      {/* Users List */}
      <div className="admin-section">
        <h2>Users ({users.length})</h2>
        <div className="users-list">
          {users.slice(0, 10).map(user => (
            <div key={user.id} className="user-card">
              <div className="user-info">
                <strong>{user.name}</strong>
                <span className="user-email">{user.email}</span>
                <span className="user-level">Level {user.level}</span>
              </div>
              <div className="user-stats">
                <span>{user.points} points</span>
                <span className={user.isActive ? 'active' : 'inactive'}>
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Admin;
